import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, m, motion } from "framer-motion";

import S from "./ScrollBlock.styled";
import { Flex, MainSection, motionAnimation } from "@/styles/global";
import { DisplayHeadingXS } from "@/styles/typography";
import { Image, Wysiwyg } from "@/styles/components";
import { IWPImage, IWPVideo } from "@/src/types";
import Youtube from "@/components/global-components/Youtube";

export interface ScrollBlockProps {
  blockId: string;
  textPosition: "left" | "right";
  blocks: {
    image: IWPImage;
    mediaType: "image" | "video";
    video: IWPVideo;
    content: {
      title: string;
      icon: IWPImage;
      text: string;
    }[];
  }[];
}

const ScrollBlock: React.FC<ScrollBlockProps> = ({
  blocks,
  blockId = "",
  textPosition,
}) => {
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageMargin, setImageMargin] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 820;
      const halfScreenHeight = isMobile
        ? window.innerHeight * 0.75
        : window.innerHeight / 2;
      const blockPositions = blockRefs.current.map((ref) => {
        const rect = ref?.getBoundingClientRect();
        return {
          top: rect?.top ?? 0,
          bottom: rect?.bottom ?? 0,
        };
      });

      const inViewIndex = blockPositions.findIndex(
        ({ top, bottom }) =>
          top <= halfScreenHeight && bottom >= halfScreenHeight
      );

      if (inViewIndex !== -1 && inViewIndex !== activeIndex) {
        setActiveIndex(inViewIndex);
      }

      const scrollPosition = window.scrollY;
      const componentTop =
        (blockRefs.current[0]?.getBoundingClientRect().top ?? 0) +
          window.scrollY ?? 0;
      const componentScrolled = scrollPosition - componentTop;
      if (componentScrolled > window.innerHeight / 2) {
        setImageMargin(true);
      } else {
        setImageMargin(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeIndex]);

  return (
    <S.Section as={motion.section} {...motionAnimation} id={blockId}>
      <Flex>
        <S.Background $textPosition={textPosition} />
        <S.Wrapper>
          <S.ScrollBlock $textPosition={textPosition}>
            <S.StickyImageWrapper $margin={imageMargin}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={
                    blocks[activeIndex].mediaType === "video"
                      ? "motion-video"
                      : ""
                  }
                >
                  {blocks[activeIndex].mediaType === "video" ? (
                    <div>
                      <Youtube video={blocks[activeIndex].video} />
                    </div>
                  ) : (
                    <Image
                      image={blocks[activeIndex].image}
                      height="100%"
                      width="auto"
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </S.StickyImageWrapper>

            <S.TextWrapper>
              {blocks.map((block, i) => {
                const isLastBlock = i === blocks.length - 1;
                return (
                  <Flex
                    key={i}
                    $column
                    ref={(el) => (blockRefs.current[i] = el)}
                    $margin={isLastBlock ? "16px" : "0 16px 140px"}
                    $marginDesktop={isLastBlock ? "16px" : "0 16px 176px"}
                  >
                    {block.content?.map((content, index) => (
                      <Flex $column $margin="0 0 48px" key={index}>
                        <Flex
                          $width="100%"
                          $justify="between"
                          $align="center"
                          $margin="0 0 16px 0"
                          $gap={16}
                        >
                          <DisplayHeadingXS
                            color="creme"
                            style={{ textAlign: "left" }}
                          >
                            {content.title}
                          </DisplayHeadingXS>
                          {content?.icon && <Image image={content.icon} />}
                        </Flex>
                        <Wysiwyg size={16} color="creme" html={content.text} />
                      </Flex>
                    ))}
                  </Flex>
                );
              })}
            </S.TextWrapper>
          </S.ScrollBlock>
        </S.Wrapper>
      </Flex>
    </S.Section>
  );
};

export default ScrollBlock;
